// NativePaywall — iOS-only Apple In-App-Purchase paywall (via RevenueCat).
//
// Rendered INSTEAD of the Stripe EmbeddedCheckout when the app is
// running inside the iOS Capacitor shell. Web users never see this
// component (they use Stripe with coupon codes).
//
// Design rules
// ------------
//  * Copy + visual language mirror Stripe path 1:1 — same "Most Saving"
//    badge, same monthly-price-callout style, same shield line at the
//    bottom. Users switching between web / iOS see the same brand.
//  * Product prices come from Apple (via RevenueCat's `product.priceString`)
//    — we NEVER hard-code prices in the iOS build. Apple would reject
//    otherwise; also, this respects the user's App Store region.
//  * Restore Purchases button is ALWAYS visible (Apple Guideline 3.1.1).
//  * After a successful purchase, we call `/api/revenuecat/reconcile`
//    so backend state is authoritative before the user proceeds.

import { useEffect, useRef, useState } from "react";
import { Sparkles, ShieldCheck, RefreshCw, ArrowLeft, Ticket, Camera, Brain, Target, Activity, Heart, Star, Zap, Trophy, Utensils, Award } from "lucide-react";
import { toast } from "sonner";
import {
  configureRevenueCat,
  getOfferingPackages,
  purchasePackage,
  restorePurchases,
  presentCodeRedemptionSheet,
  syncPurchases,
  addCustomerInfoUpdateListener,
  removeCustomerInfoUpdateListener,
  isRevenueCatEnabled,
  setAttributes,
} from "@/lib/revenuecat";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n.jsx";
import AllInOneHub from "@/components/AllInOneHub";
import { usePaywallConfig } from "@/components/usePaywallConfig";
import { useFeatureFlag } from "@/lib/featureFlags";
import { track as trackEvent } from "@/lib/analytics";
import { hapticLight, hapticMedium, hapticSuccess, hapticError } from "@/lib/haptics";
import { openExternal, nativePlatform } from "@/lib/native";
import { sendEvent as pbSendEvent } from "@/lib/postback";
import {
  trackInitiateCheckout as afTrackCheckout,
  trackPurchase as afTrackPurchase,
  trackStartTrial as afTrackTrial,
} from "@/lib/appsflyer";
import { requestATT, getATTStatus, isATTSupported } from "@/lib/att";
import api from "@/lib/api";

// Play Store package name — used to deep-link the Play Store app on
// Android for promo-code redemption. Kept as a constant so ProGuard/R8
// minification can't accidentally rename it.
const ANDROID_PACKAGE_NAME = "com.calofay.app";

// Icon map for the server-driven features list. Add new icons here to
// expose them to admins — the value in Mongo is a string like "camera".
const FEATURE_ICONS = {
  camera: Camera, brain: Brain, target: Target, activity: Activity,
  heart: Heart, star: Star, zap: Zap, trophy: Trophy, utensils: Utensils,
  award: Award, sparkles: Sparkles, shield: ShieldCheck,
};

// Map internal error reasons → user-facing Arabic copy. We surface these
// instead of a generic error so support can triage a stuck paywall
// directly from the user's screenshot (no need to attach Safari Web
// Inspector). Reasons come from `getOfferingPackages().reason`.
function reasonToMessage(reason, lang = "ar") {
  const en = lang === "en";
  if (!reason) return en ? "No subscription plans available." : "لا تتوفر خطط اشتراك حالياً.";
  if (reason === "no_current_offering")
    return en ? "No active subscription offering configured. Try again later." : "لم يتم إعداد باقة اشتراك مفعّلة. حاول لاحقاً.";
  if (reason === "offering_has_no_packages")
    return en ? "The offering has no products. Try again later." : "الباقة لا تحتوي على منتجات. حاول لاحقاً.";
  if (reason.startsWith("error:"))
    return en ? "Couldn't reach the App Store. Check your internet and try again." : "تعذر الاتصال بمتجر Apple. تحقق من الاتصال بالإنترنت وأعد المحاولة.";
  return en ? "Couldn't load subscriptions. Try again later." : "تعذر تحميل الاشتراكات. حاول لاحقاً.";
}

const ACCESS_STATUSES = new Set(["active", "trialing", "cancellation_pending"]);

// After a successful Apple purchase, sandbox (and rarely production)
// can take a few seconds before the RevenueCat webhook writes
// `subscription_status = "active"` to our DB. We retry reconcile +
// refresh with backoff so the paywall never leaves the user hanging.
// Poll reconcile + refresh with backoff to verify premium is active.
// `windowMs` controls the max time to wait (buy = ~11s since purchase is
// complete before we start polling; redeem = ~35s since the user may
// still be typing when we begin).
//
// When called from the promo-code redeem path we ALSO call
// `syncPurchases()` on each iteration. Apple's Offer Code redemption
// completes purely on the device — RC's webhook only fires after the
// receipt is pushed up, which normally happens on next app foreground.
// If we don't force-push it, users hit the exact bug the customer
// reported: loading spinner forever until they tap Restore Purchases.
// `syncPurchases()` is the silent equivalent of tapping Restore — it
// pushes the local receipt, RC fires the webhook, backend flips to
// `active`, our next reconcile+refresh call sees it, done.
//
// `shouldAbort` is an optional callback (evaluated before each sleep
// + before each network call) so the redeem flow can break out of the
// loop the instant the parallel customer-info listener wins the race.
// Without it, up to 8 extra syncPurchases+reconcile round-trips fire
// after success and the paywall keeps `busy=true` for the full 35s.
async function confirmSubscriptionActive(refresh, windowMs = 11000, shouldAbort = null) {
  const buyDelays    = [0, 1000, 2000, 3000, 5000];                    // 11s
  const redeemDelays = [0, 2000, 3000, 3000, 4000, 5000, 6000, 6000, 6000]; // ~35s
  const isRedeem = windowMs >= 20000;
  const delays = isRedeem ? redeemDelays : buyDelays;
  for (let i = 0; i < delays.length; i++) {
    if (shouldAbort && shouldAbort()) return true; // listener already fired
    if (delays[i] > 0) await new Promise((r) => setTimeout(r, delays[i]));
    if (shouldAbort && shouldAbort()) return true;
    // Redeem path: push the local Apple receipt to RC before every
    // reconcile so we don't wait for the app to backgrounded/foreground
    // for RC to notice the purchase. This is what "tap Restore" was
    // doing manually for our users — now automated.
    if (isRedeem) {
      try { await syncPurchases(); } catch (_) {}
    }
    if (shouldAbort && shouldAbort()) return true;
    try { await api.post("/revenuecat/reconcile"); } catch (_) {}
    let u = null;
    try { u = await refresh(); } catch (_) {}
    const status = String(u?.subscription_status || "").toLowerCase();
    if (ACCESS_STATUSES.has(status)) return true;
  }
  return false;
}

// Map RevenueCat packageType → Arabic label matching the web design.
// Calofay web sells Quarterly (3 months) + Annual (12 months) — NOT
// monthly — so the packageType labels/detection mirror those two.
function packageLabel(pkg, lang = "ar") {
  const t = String(pkg.packageType || pkg.identifier || "").toLowerCase();
  const en = lang === "en";
  if (t.includes("annual") || t === "$rc_annual") return en ? "12 Months" : "12 شهر";
  if (t.includes("three_month") || t.includes("quarter") || t === "$rc_three_month") return en ? "3 Months" : "3 شهور";
  if (t.includes("month") || t === "$rc_monthly") return en ? "Monthly" : "شهري";
  return pkg.product?.title || (en ? "Subscription" : "اشتراك");
}

function packageBillingCadence(pkg, lang = "ar") {
  const t = String(pkg.packageType || pkg.identifier || "").toLowerCase();
  const en = lang === "en";
  if (t.includes("annual") || t === "$rc_annual") return en ? "yearly" : "سنوياً";
  if (t.includes("three_month") || t.includes("quarter") || t === "$rc_three_month") return en ? "per 3 months" : "كل 3 أشهر";
  if (t.includes("month") || t === "$rc_monthly") return en ? "monthly" : "شهرياً";
  return "";
}

function isAnnualPackage(pkg) {
  const t = String(pkg.packageType || pkg.identifier || "").toLowerCase();
  return t.includes("annual");
}

// How many months does one billing cycle cover? Used to convert the
// posted price into a marketing "per-month" figure (e.g. $59.99/yr
// becomes $4.99/month — much lighter on the eye).
function packageMonthCount(pkg) {
  const t = String(pkg.packageType || pkg.identifier || "").toLowerCase();
  if (t.includes("annual") || t === "$rc_annual") return 12;
  if (t.includes("six_month") || t === "$rc_six_month") return 6;
  if (t.includes("three_month") || t.includes("quarter") || t === "$rc_three_month") return 3;
  if (t.includes("two_month") || t === "$rc_two_month") return 2;
  if (t.includes("month") || t === "$rc_monthly") return 1;
  if (t.includes("weekly") || t === "$rc_weekly") return 0.25;  // approx
  return 1;
}

// Format a raw price back into the same currency/style Apple used, so
// the "per-month" price sits next to the sticker price without any
// jarring locale mismatch. Falls back to a plain number if Intl can't
// format the currencyCode (unknown codes are surprisingly common on
// TestFlight sandbox accounts from odd regions).
function formatMoney(amount, currencyCode) {
  try {
    if (currencyCode) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyCode,
        // 2 decimals for most currencies; Intl auto-adjusts (JPY → 0, KWD → 3).
        maximumFractionDigits: 2,
      }).format(amount);
    }
  } catch (_) { /* fall through */ }
  return `$${amount.toFixed(2)}`;
}

// Localized currency long-name — "ريال سعودي" / "US Dollar" / "درهم إماراتي".
// Powers the small label under the big monthly-equivalent number so
// the layout matches the marketing paywall from the web. Falls back
// to the ISO code (`SAR`, `USD`) on browsers without `DisplayNames`.
function currencyLabel(code, lang) {
  if (!code) return "";
  try {
    return new Intl.DisplayNames([lang === "en" ? "en" : "ar"], { type: "currency" }).of(code) || code;
  } catch (_) {
    return code;
  }
}

// Format just the amount portion — the currency name is displayed
// separately. Rounds to whole units for the marketing headline (`21
// ريال سعودي` reads much better than `20.58 ريال سعودي`). Uses the
// local number script (Arabic digits for `ar`, ASCII for `en`).
function formatAmount(amount, lang) {
  try {
    return new Intl.NumberFormat(lang === "en" ? "en-US" : "ar-SA", {
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  } catch (_) {
    return String(Math.round(amount));
  }
}

// "$4.99/month" equivalent for the plan card. Returns null for the
// monthly plan (no need to show — user already sees the monthly price).
function packageMonthlyPrice(pkg, lang) {
  const months = packageMonthCount(pkg);
  if (months <= 1) return null;  // hide for monthly / weekly
  const raw = Number(pkg?.product?.price);
  if (!raw || !isFinite(raw)) return null;
  const perMonth = raw / months;
  const money = formatMoney(perMonth, pkg?.product?.currencyCode);
  return lang === "en" ? `${money}/month` : `${money}/شهر`;
}

// Split the per-month price into `{amount, currencyLabel}` so the card
// can render the number huge and the currency name small/underneath —
// matching the marketing layout used on the web `LandingV2` paywall
// (Cal AI / Noom style).
function packageMonthlyPriceParts(pkg, lang) {
  const months = packageMonthCount(pkg);
  if (months <= 1) return null;
  const raw = Number(pkg?.product?.price);
  if (!raw || !isFinite(raw)) return null;
  const perMonth = raw / months;
  return {
    amount: formatAmount(perMonth, lang),
    currencyLabel: currencyLabel(pkg?.product?.currencyCode, lang),
  };
}

// Anchor-price for the strikethrough — the per-month rate of the
// SHORTEST available plan multiplied by the current plan's month count.
// This gives us an *honest* "what you'd pay at the higher rate" number
// (not a fabricated markup). Returns a pre-formatted string in the
// same "149 ريال سعودي" style used by the total row for visual
// consistency. Returns null when there's no cheaper baseline.
function packageAnchorTotal(pkg, packages, lang) {
  if (!Array.isArray(packages) || packages.length < 2) return null;
  const thisMonths = packageMonthCount(pkg);
  if (thisMonths <= 1) return null;
  const others = packages
    .filter((p) => p.identifier !== pkg.identifier)
    .map((p) => ({ pkg: p, months: packageMonthCount(p), raw: Number(p?.product?.price) || 0 }))
    .filter((x) => x.raw > 0 && x.months > 0 && x.months < thisMonths);
  if (!others.length) return null;
  others.sort((a, b) => a.months - b.months);
  const baseline = others[0];
  const anchorRaw = (baseline.raw / baseline.months) * thisMonths;
  const actualRaw = Number(pkg?.product?.price) || 0;
  if (anchorRaw <= actualRaw * 1.05) return null;
  return `${formatAmount(anchorRaw, lang)} ${currencyLabel(pkg?.product?.currencyCode, lang)}`;
}

// Format the ACTUAL total in the same "149 ريال سعودي" style as the
// anchor so both rows align visually. Falls back to Apple's raw
// `priceString` if we can't parse the numeric price for any reason.
function packageTotalDisplay(pkg, lang) {
  const raw = Number(pkg?.product?.price);
  const code = pkg?.product?.currencyCode;
  if (!raw || !isFinite(raw) || !code) return pkg?.product?.priceString || "";
  return `${formatAmount(raw, lang)} ${currencyLabel(code, lang)}`;
}

// Compare the per-month price of the annual plan against the shortest
// non-annual plan (usually 3-month) to compute a "Save X%" savings
// figure. Returns null if we don't have both to compare against — the
// UI hides the badge in that case rather than showing a lie.
function annualSavingsPct(packages) {
  const annual = packages.find(isAnnualPackage);
  if (!annual) return null;
  const others = packages
    .filter((p) => !isAnnualPackage(p))
    .map((p) => ({
      pkg: p,
      months: packageMonthCount(p),
      raw: Number(p?.product?.price) || 0,
    }))
    .filter((x) => x.raw > 0 && x.months > 0);
  if (!others.length) return null;
  // Use the shortest cycle as the baseline — that's the most expensive
  // per-month rate, which makes annual look best.
  others.sort((a, b) => a.months - b.months);
  const baseline = others[0];
  const baselinePerMonth = baseline.raw / baseline.months;
  const annualPerMonth = Number(annual?.product?.price) / 12;
  if (!(baselinePerMonth > 0 && annualPerMonth > 0)) return null;
  const pct = Math.round(((baselinePerMonth - annualPerMonth) / baselinePerMonth) * 100);
  // Only surface if it's actually a saving — negative or tiny savings
  // aren't worth shouting about.
  return pct >= 5 ? pct : null;
}

// Detect a free-trial introductory offer on the Apple product. Users see
// "Start 3-day Free Trial" instead of "Subscribe" — matching how Cal AI,
// Noom, and BetterMe convert 3-5× better on iOS.
//
// STRICT check — must be explicitly free ($0.00) OR the StoreKit
// `paymentMode` must literally be `freeTrial`. Previously any intro
// offer with a `periodUnit` counted, which incorrectly captured PAID
// intro discounts (e.g. "$0.99 first month, then $9.99/mo"). Showing
// "Start N days FREE" for a paid intro is misleading and a documented
// Apple 3.1.2 rejection risk.
function hasFreeTrial(pkg) {
  const intro = pkg?.product?.introPrice;
  if (!intro) return false;
  const mode = String(intro.paymentMode || "").toLowerCase();
  if (mode === "freetrial" || mode === "free_trial") return true;
  // Some Apple environments omit paymentMode but set price to 0 for
  // free trials — treat that as free trial too.
  if ((intro.price === 0 || intro.priceString === "$0.00") && intro.periodUnit) return true;
  return false;
}

// Arabic plural handling — Arabic distinguishes singular / dual (2) /
// small plural (3-10) / large plural (11+). For the few units we care
// about here, the rules are:
//   1  → يوم / أسبوع / شهر
//   2  → يومان / أسبوعان / شهران
//   3-10 → 3 أيام / 3 أسابيع / 3 أشهر
//   11+ → 11 يوماً / 11 أسبوعاً / 11 شهراً
function _arabicUnitLabel(units, unit) {
  const u = String(unit || "day").toLowerCase();
  const isDay = u.includes("day");
  const isWeek = u.includes("week");
  const isMonth = u.includes("month");
  if (units === 1) return isDay ? "يوم" : isWeek ? "أسبوع" : isMonth ? "شهر" : `${units}`;
  if (units === 2) return isDay ? "يومان" : isWeek ? "أسبوعان" : isMonth ? "شهران" : `${units}`;
  if (units >= 3 && units <= 10) {
    const plural = isDay ? "أيام" : isWeek ? "أسابيع" : isMonth ? "أشهر" : "";
    return `${units} ${plural}`;
  }
  const large = isDay ? "يوماً" : isWeek ? "أسبوعاً" : isMonth ? "شهراً" : "";
  return `${units} ${large}`;
}

function trialLengthLabel(pkg, isRTL) {
  const intro = pkg?.product?.introPrice;
  const units = intro?.periodNumberOfUnits || 3;
  const unit = String(intro?.periodUnit || "day").toLowerCase();
  if (isRTL) return _arabicUnitLabel(units, unit);
  // English pluralisation is simpler — pluralise the unit when > 1.
  const unitWord = unit.includes("day") ? "day" : unit.includes("week") ? "week" : unit.includes("month") ? "month" : unit;
  return units === 1 ? `1-${unitWord}` : `${units}-${unitWord}s`;
}

export default function NativePaywall({ onSuccess }) {
  const { user, refresh } = useAuth();
  const { lang } = useI18n();
  const en = lang === "en";
  const paywall = usePaywallConfig(lang);
  const pw = paywall?.config || {};
  const pwOpts = pw.options || {};
  const showRedeemCode = useFeatureFlag("redeem_code_button");
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);
  const [redeemCodeInput, setRedeemCodeInput] = useState("");
  // Guards against calling onSuccess twice — once from the RC customer-
  // info listener (which fires as soon as the SDK sees the entitlement
  // update from Apple, ~1-3s after redemption completes) and once from
  // the polling loop (as a fallback if the listener never fires).
  const successFiredRef = useRef(false);

  useEffect(() => {
    // Fire once per mount so we can compute paywall→trial conversion
    // per variant. Variant name comes from the server-driven config
    // and is included so A/B experiments are attributable.
    trackEvent("paywall_viewed", {}, { variant: paywall?.variant });

    // Apple's App Tracking Transparency popup — highest-intent moment
    // (user is about to see a price) is the ideal place to request
    // permission. Apple only shows the prompt ONCE per install, so we
    // check `getATTStatus()` first and only trigger the popup when the
    // status is still `notDetermined`. Users who tap "Allow" give us
    // 30-40% more accurate Meta / TikTok attribution; users who deny
    // still work fine via SKAN 4. This runs async in the background
    // and never blocks the paywall render.
    (async () => {
      try {
        if (!isATTSupported()) return;
        const status = await getATTStatus();
        if (status === "notDetermined") {
          await requestATT();
          // AppsFlyer's `waitForATTUserAuthorization=60` init flag was
          // waiting for exactly this — the SDK will now include the
          // ATT decision in its very first install postback to Meta.
        }
      } catch (_) { /* attribution is best-effort, never block UI */ }
    })();

    let alive = true;
    async function boot() {
      try {
        // Wrap the ENTIRE boot flow (configure + getOfferings) in a
        // single 10-second timeout. Previously the timeout only guarded
        // `getOfferings`, but if `Purchases.configure()` hangs (a known
        // Capacitor plugin registration edge case) the timeout never
        // fires and the paywall spins forever.
        const bootWork = (async () => {
          if (user?.id) await configureRevenueCat(user.id);
          // Attribution: write the affiliate code from RedeemRedirect
          // (or any /join/:code link) as a RevenueCat subscriber
          // attribute so RC includes it on every future INITIAL_PURCHASE,
          // RENEWAL, and CANCELLATION webhook for this user. Community-
          // recommended pattern that DOESN'T depend on the raw Apple
          // offer_code string (StoreKit surfaces it inconsistently).
          try {
            const aff = localStorage.getItem("calofay_affiliate_code");
            if (aff) await setAttributes({ calofay_affiliate: aff.toUpperCase() });
          } catch (_) {}
          return await getOfferingPackages();
        })();
        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("boot_timeout")), 10000)
        );
        const result = await Promise.race([bootWork, timeout]);
        if (!alive) return;
        const pkgs = result.packages || [];
        setPackages(pkgs);
        setSelected(pkgs.find(isAnnualPackage) || pkgs[0] || null);
        if (!pkgs.length) {
          // Show an explicit error whether or not the SDK gave us a
          // structured reason — otherwise the paywall renders with the
          // "Choose your plan" heading and zero rows below, leaving
          // the user staring at an empty sheet with no CTA and no idea
          // what went wrong.
          setError(
            result.reason
              ? reasonToMessage(result.reason, lang)
              : (en ? "No subscription plans are available right now. Try again later." : "لا توجد خطط اشتراك متاحة حالياً. حاول لاحقاً.")
          );
        }
      } catch (e) {
        console.error("[NativePaywall] boot failed", e);
        if (alive) setError(
          e?.message === "boot_timeout"
            ? (en ? "Loading subscriptions is slow. Check your internet and try again." : "تأخر تحميل الاشتراكات. تأكد من اتصالك بالإنترنت وأعد المحاولة.")
            : (en ? "Couldn't load subscriptions. Try again later." : "تعذر تحميل الاشتراكات. حاول لاحقاً.")
        );
      } finally {
        if (alive) setLoading(false);
      }
    }
    boot();
    return () => { alive = false; };
  }, [user?.id]);

  const buy = async () => {
    if (!selected || busy) return;
    hapticMedium();
    // Track the moment the user commits to a plan — before Apple's
    // sheet appears — so we can measure abandon-rate at the sheet.
    trackEvent("plan_selected", {
      package_id: selected.identifier,
      is_annual: isAnnualPackage(selected),
      price: selected?.product?.price,
      currency: selected?.product?.currencyCode,
      trial: hasFreeTrial(selected),
    }, { variant: paywall?.variant });
    setBusy(true);
    setError(null);
    successFiredRef.current = false;

    // Postback: `initiate_checkout` is the MMP-standard funnel event
    // that fires when the user commits to a plan (before Apple's
    // sheet appears). This is the step Postback correlates back to
    // an ASA keyword tap and drops in the "Checkout Started" bucket
    // of the activation funnel.
    pbSendEvent("initiate_checkout", {
      product_id: selected.identifier,
      revenue: selected?.product?.price,
      currency: selected?.product?.currencyCode,
    });

    // AppsFlyer: dual-track the same funnel step so Meta's Ads
    // Manager gets the signal via AppsFlyer's Meta Conversions API
    // integration. AppsFlyer dedupes on transactionId later, so an
    // initiate_checkout that never converts still counts as a
    // funnel drop-off (Meta uses this for retargeting audiences).
    afTrackCheckout({
      productId: selected.identifier,
      revenue: selected?.product?.price,
      currency: selected?.product?.currencyCode,
    });

    // Local helper — fires onSuccess exactly once regardless of which
    // path (listener or poller) resolves first.
    const finishOnce = () => {
      if (successFiredRef.current) return;
      successFiredRef.current = true;
      hapticSuccess();
      toast.success(en ? "Your subscription is active 🎉" : "تم تفعيل اشتراكك 🎉");
      onSuccess?.();
    };

    // Same customer-info listener trick as the redeem flow — RC's
    // local SDK sees the entitlement flip ~1-3s after Apple's sheet
    // closes with a successful purchase, faster than our webhook can
    // hit /revenuecat/reconcile. Listener + poller race; whichever
    // wins fires onSuccess, the other becomes a no-op.
    let listenerId = null;
    try {
      listenerId = await addCustomerInfoUpdateListener(async (_ci, hasActive) => {
        if (!hasActive || successFiredRef.current) return;
        try { await api.post("/revenuecat/reconcile"); } catch (_) {}
        try {
          const u = await refresh();
          const status = String(u?.subscription_status || "").toLowerCase();
          if (ACCESS_STATUSES.has(status)) finishOnce();
        } catch (_) {}
      });
    } catch (_) { /* non-fatal, poll fallback still runs */ }

    try {
      const result = await purchasePackage(selected);
      if (result.cancelled) {
        trackEvent("purchase_cancelled", { package_id: selected.identifier }, { variant: paywall?.variant });
        return;
      }
      trackEvent(hasFreeTrial(selected) ? "trial_started" : "purchase_success", {
        package_id: selected.identifier,
        price: selected?.product?.price,
        currency: selected?.product?.currencyCode,
      }, { variant: paywall?.variant });

      // Postback: fire `start_trial` OR `purchase` immediately from
      // the client for a faster funnel signal. RC will also fire the
      // authoritative event server-side via webhook (with the true
      // net-of-refund revenue) — Postback deduplicates on the RC
      // transaction id, so this client-side hint just makes the
      // dashboard react in seconds instead of minutes.
      pbSendEvent(hasFreeTrial(selected) ? "start_trial" : "purchase", {
        product_id: selected.identifier,
        revenue: selected?.product?.price,
        currency: selected?.product?.currencyCode,
      });

      // AppsFlyer: same story. Also updates the SKAN 4 conversion
      // value tier so Meta's SKAN-based bidding can react. RC's
      // server-side integration will re-fire the authoritative event
      // with net-of-refund revenue; AppsFlyer dedupes on the AF
      // transaction id.
      if (hasFreeTrial(selected)) {
        afTrackTrial({
          productId: selected.identifier,
          currency: selected?.product?.currencyCode,
        });
      } else {
        afTrackPurchase({
          productId: selected.identifier,
          revenue: selected?.product?.price,
          currency: selected?.product?.currencyCode,
        });
      }
      const confirmed = await confirmSubscriptionActive(
        refresh,
        11000,
        () => successFiredRef.current,
      );
      if (!confirmed && !successFiredRef.current && !result.success) {
        setError(en ? "Payment received but verification is delayed. Tap 'Restore purchases'." : "تم الدفع لكن التحقق تأخر. اضغط 'استعادة المشتريات'.");
        return;
      }
      finishOnce();
    } catch (e) {
      console.error("[NativePaywall] purchase failed", e);
      trackEvent("purchase_failed", { package_id: selected?.identifier, reason: e?.message?.slice(0, 100) }, { variant: paywall?.variant });
      hapticError();
      setError(e?.message || (en ? "An error occurred during purchase. Please try again." : "حدث خطأ أثناء الشراء. حاول مرة أخرى."));
    } finally {
      if (listenerId) { try { await removeCustomerInfoUpdateListener(listenerId); } catch (_) {} }
      setBusy(false);
    }
  };

  const restore = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await restorePurchases();
      if (result.success) {
        try { await api.post("/revenuecat/reconcile"); } catch (_) {}
        try { await refresh(); } catch (_) {}
        toast.success(en ? "Your subscription is restored" : "تم استعادة اشتراكك");
        onSuccess?.();
      } else {
        // RC said no active entitlement — but the user's Apple ID may
        // hold an active subscription that just hasn't been linked to
        // this app_user_id yet (common on fresh installs, or when
        // subscription bought under a different in-app account).
        // Call our backend reconcile as a fallback — the RC v2 API may
        // have caught up server-side even when the client CustomerInfo
        // cache hasn't. If the DB comes back "active" after that, we
        // treat it as a successful restore.
        try { await api.post("/revenuecat/reconcile"); } catch (_) {}
        let u = null;
        try { u = await refresh(); } catch (_) {}
        const status = String(u?.subscription_status || "").toLowerCase();
        if (ACCESS_STATUSES.has(status)) {
          toast.success(en ? "Your subscription is restored" : "تم استعادة اشتراكك");
          onSuccess?.();
        } else {
          const isAndroid = nativePlatform() === "android";
          const account = isAndroid
            ? (en ? "Google Play account" : "حسابك على Google Play")
            : (en ? "Apple account" : "حسابك على Apple");
          toast.info(en ? `No prior subscription linked to your ${account}.` : `لا يوجد اشتراك سابق مرتبط بـ${account}.`);
        }
      }
    } catch (e) {
      console.error("[NativePaywall] restore failed", e);
      setError(e?.message || (en ? "Couldn't restore purchases." : "تعذر استعادة المشتريات."));
    } finally {
      setBusy(false);
    }
  };

  // Apple Offer Code redemption:
  //   1. Register a customer-info listener so we can react the moment
  //      RevenueCat's SDK sees the entitlement flip (~1-3s after the
  //      user submits the code in Apple's sheet — much faster than
  //      waiting for the RC webhook to hit our backend).
  //   2. Present Apple's built-in redemption sheet (native UI).
  //   3. In parallel we ALSO poll `/revenuecat/reconcile` with
  //      `syncPurchases()` each iteration (see `confirmSubscriptionActive`)
  //      so the flow never depends purely on either the listener or
  //      the poller — belt-and-suspenders. `syncPurchases()` is what
  //      the user was manually doing when they tapped "Restore" —
  //      now automated so the paywall closes on its own.
  //   4. Whichever wins fires `onSuccess()` first — the other becomes
  //      a no-op via `successFiredRef`.
  //   5. If nothing changed after 35s (user cancelled the sheet,
  //      entered a bad code, etc.) we stay silent — the sheet
  //      handled its own errors.
  const redeem = async () => {
    if (busy) return;
    const platform = nativePlatform();

    // ── iOS path — RESTORED to Apple's native redemption sheet ─────
    // Per user instruction: iPhone users MUST redeem inside Apple's
    // sheet (not web) so Apple's Anti-Steering policy is fully honoured
    // and no App Store review flag is raised. This is the exact flow
    // that shipped with the App Store launch.
    if (platform === "ios") {
      if (!isRevenueCatEnabled()) {
        toast.error(en ? "Promo codes are only available inside the Calofay iOS app." : "رموز الخصم متاحة داخل تطبيق كالوفاي على الآيفون فقط.");
        return;
      }
      setBusy(true);
      setError(null);
      successFiredRef.current = false;
      const finishOnce = () => {
        if (successFiredRef.current) return;
        successFiredRef.current = true;
        toast.success(en ? "Promo code applied — your subscription is active 🎉" : "تم تطبيق رمز الخصم — اشتراكك مفعّل 🎉");
        onSuccess?.();
      };
      let listenerId = null;
      try {
        listenerId = await addCustomerInfoUpdateListener(async (_ci, hasActive) => {
          if (!hasActive || successFiredRef.current) return;
          try { await api.post("/revenuecat/reconcile"); } catch (_) {}
          try {
            const u = await refresh();
            const status = String(u?.subscription_status || "").toLowerCase();
            if (ACCESS_STATUSES.has(status)) finishOnce();
          } catch (_) {}
        });
      } catch (_) { /* non-fatal, poll fallback still runs */ }
      try {
        await presentCodeRedemptionSheet();
        // Apple's sheet closed. We DON'T know whether the user
        // actually entered a code or just tapped the X — Apple
        // doesn't tell us. So release the busy state right now so
        // the paywall stays interactive (change plan, tap Subscribe,
        // tap the ticket again…) and let the listener + silent poll
        // run in the background. If a real redemption succeeded, the
        // customer-info-update listener fires within ~2s and
        // `finishOnce()` triggers success. If the user just dismissed,
        // nothing fires and the paywall is already usable again.
        setBusy(false);
        const confirmed = await confirmSubscriptionActive(refresh, 35000, () => successFiredRef.current);
        if (confirmed) finishOnce();
      } catch (e) {
        console.error("[NativePaywall] redeem failed", e);
        if (String(e?.message || "").includes("unavailable")) {
          toast.error(en ? "Promo codes are unavailable right now — please try again later." : "رموز الخصم غير متاحة الآن — حاول لاحقاً.");
        }
      } finally {
        if (listenerId) { try { await removeCustomerInfoUpdateListener(listenerId); } catch (_) {} }
        // Safety net in case the `await presentCodeRedemptionSheet()`
        // itself threw before the inline `setBusy(false)` ran.
        setBusy(false);
      }
      return;
    }

    // ── Android + web path — open the code input modal, then jump
    // to the web Stripe checkout with the user's name + email
    // pre-filled from their profile (they always signed up before
    // reaching the paywall, so we already have both fields — no need
    // for an extra pre-paywall capture screen).
    setRedeemCodeInput("");
    setRedeemModalOpen(true);
  };

  // Submit handler for the redeem-code modal — hits the public
  // `/join/{code}` route which we already built to auto-redirect to
  // Stripe checkout with the coupon pre-applied.
  const submitRedeemCode = () => {
    const code = (redeemCodeInput || "").trim().toUpperCase();
    if (!code) {
      toast.error(en ? "Enter a code to continue" : "أدخل الرمز للمتابعة");
      return;
    }
    // Pull name + email from the authenticated user's profile — they
    // signed up before ever reaching the paywall, so we already have
    // both fields. Pre-fills the web checkout so the visitor lands
    // directly on the plan selection + Stripe payment page.
    const name = (user?.name || user?.full_name || "").trim();
    const email = (user?.email || "").trim();
    const params = new URLSearchParams();
    if (name) params.set("name", name);
    if (email) params.set("email", email);
    const qs = params.toString();
    const url = `https://calofay.app/join/${encodeURIComponent(code)}${qs ? `?${qs}` : ""}`;
    setRedeemModalOpen(false);
    openExternal(url);
    toast.info(
      en
        ? "Opening web checkout — complete payment there, then come back and tap 'Refresh'."
        : "جارٍ فتح صفحة الدفع — أكمل العملية ثم ارجع واضغط \"تحديث الحالة\".",
      { duration: 8000 }
    );
  };

  if (loading) {

    // Skeleton state — mimics the two-plan layout so the transition
    // to real cards feels instant (no reflow) once RevenueCat responds.
    // Beats a spinner-of-death for premium native feel.
    return (
      <div className="space-y-3" data-testid="native-paywall-loading">
        {/* hero skeleton */}
        <div className="space-y-2 mb-3">
          <div className="h-6 w-3/4 bg-neutral-100 rounded-lg animate-pulse" />
          <div className="h-3 w-full bg-neutral-100 rounded animate-pulse" />
        </div>
        {/* two plan skeletons */}
        {[0, 1].map((i) => (
          <div key={i} className="rounded-3xl border-2 border-neutral-100 p-5 flex items-center gap-3">
            <div className="w-5 h-5 rounded-full bg-neutral-100 animate-pulse flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-32 bg-neutral-100 rounded animate-pulse" />
              <div className="h-3 w-48 bg-neutral-100 rounded animate-pulse" />
            </div>
            <div className="text-end space-y-2">
              <div className="h-6 w-20 bg-neutral-100 rounded animate-pulse" />
              <div className="h-3 w-16 bg-neutral-100 rounded animate-pulse" />
            </div>
          </div>
        ))}
        {/* CTA skeleton */}
        <div className="h-14 w-full bg-neutral-100 rounded-full animate-pulse mt-4" />
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-neutral-500" data-testid="native-paywall-empty">
        <p className="mb-3">{error || (en ? "No subscription plans available on this device." : "لا تتوفر خطط اشتراك حالياً على هذا الجهاز.")}</p>
        <div className="flex flex-col gap-2 items-center">
          <button
            onClick={restore}
            disabled={busy}
            className="text-brand-500 font-semibold underline disabled:opacity-60"
            data-testid="native-paywall-restore"
          >
            {en ? "Restore previous purchase" : "استعادة مشتريات سابقة"}
          </button>
          {showRedeemCode && (
            <button
              onClick={redeem}
              disabled={busy}
              className="text-neutral-500 font-medium underline disabled:opacity-60 flex items-center gap-1.5"
              data-testid="native-paywall-redeem-empty"
            >
              <Ticket className="w-3.5 h-3.5" />
              {en ? "I have a promo code" : "لدي رمز خصم"}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Computed once per render — shown on the annual plan's "Best value"
  // badge to nudge users toward the higher-margin plan. Falls back to
  // just "Best value" when there's nothing to compare against.
  const savingsPct = annualSavingsPct(packages);

  return (
    <div className="space-y-3" data-testid="native-paywall">
      {/* Server-driven hero — ONLY title now. Owner (Feb 2026)
          dropped the subtitle "جرب مجاناً 3 أيام..." because many
          Apple regions don't honour the trial on first install and
          the AllInOneHub (below) carries the value prop visually. */}
      {pw.header?.title && (
        <div className="mb-2" data-testid="native-paywall-hero">
          <div className="text-xl font-black text-neutral-900 leading-tight text-center">{pw.header.title}</div>
        </div>
      )}

      {/* Orbital "8-in-1" value prop — mirrors the web Hero section
          so paying users see the same chapter they saw on calofay.app.
          Replaces the old checkmark features list (`pw.features`)
          which was redundant with this visual. */}
      <p className="text-sm font-bold text-neutral-700 text-center leading-snug" data-testid="native-paywall-all-in-one">
        {en ? "One Calofay subscription replaces 8 apps" : "اشتراك واحد في كالوفاي يغنيك عن 8 تطبيقات"}
      </p>
      <AllInOneHub lang={lang} isRTL={lang === "ar"} compact />

      <div className="font-bold text-lg text-neutral-900 mb-1 mt-4">{en ? "Choose your plan" : "اختر خطتك"}</div>

      {packages.map((pkg) => {
        const annual = isAnnualPackage(pkg);
        const isSelected = selected?.identifier === pkg.identifier;
        const monthlyParts = pwOpts.show_monthly_equivalent === false ? null : packageMonthlyPriceParts(pkg, lang);
        const anchorTotal = packageAnchorTotal(pkg, packages, lang);
        const totalDisplay = packageTotalDisplay(pkg, lang);
        return (
          <button
            key={pkg.identifier}
            onClick={() => { hapticLight(); setSelected(pkg); }}
            className={`w-full text-start rounded-3xl border-2 transition-all p-5 relative ${
              isSelected
                ? "border-brand-500 shadow-[0_0_0_4px_rgba(255,107,53,0.1)]"
                : "border-neutral-200 hover:border-brand-300"
            }`}
            data-testid={`native-plan-${pkg.identifier}`}
          >
            {annual && (pwOpts.show_savings_badge !== false) && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 orange-gradient text-white text-xs font-black px-4 py-1 rounded-full shadow-md whitespace-nowrap" data-testid="native-plan-best-badge">
                {(() => {
                  const b = pw.badges || {};
                  const tmpl = savingsPct ? (b.save_pct_template || (en ? `Best value · Save {pct}%` : `الأكثر توفيراً · وفّر {pct}%`)) : (b.best_value || (en ? "Best value" : "الأكثر توفيراً"));
                  return savingsPct ? tmpl.replace("{pct}", String(savingsPct)) : tmpl;
                })()}
              </div>
            )}
            <div className="flex items-center gap-3">
              {/* Radio bubble */}
              <div className={`w-6 h-6 rounded-full border-2 shrink-0 flex items-center justify-center ${isSelected ? "border-brand-500 bg-brand-500" : "border-neutral-300"}`}>
                {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
              </div>

              {/* Middle column — title + anchor + total */}
              <div className="flex-1 min-w-0">
                <div className="font-black text-base sm:text-lg text-neutral-900 leading-tight truncate">
                  {packageLabel(pkg, lang)}
                </div>
                {anchorTotal && (
                  <div className="text-xs text-neutral-400 line-through num mt-1.5 whitespace-nowrap" data-testid={`native-plan-anchor-${pkg.identifier}`}>
                    {anchorTotal}
                  </div>
                )}
                {totalDisplay && (
                  <div className="text-xs sm:text-sm font-bold text-neutral-700 num mt-0.5 whitespace-nowrap">
                    {en ? `Total ${totalDisplay}` : `الإجمالي ${totalDisplay}`}
                  </div>
                )}
              </div>

              {/* Right column (RTL) — big monthly-equivalent price */}
              <div className="text-center shrink-0 min-w-[104px]">
                {monthlyParts ? (
                  <>
                    <div className="text-4xl sm:text-5xl font-black text-brand-500 num leading-none" data-testid={`native-plan-monthly-${pkg.identifier}`}>
                      {monthlyParts.amount}
                    </div>
                    <div className="text-[11px] sm:text-xs font-bold text-neutral-600 mt-1.5 leading-tight">
                      {monthlyParts.currencyLabel}
                    </div>
                    <div className="text-[10px] text-neutral-400 mt-0.5">
                      {en ? "monthly" : "شهرياً"}
                    </div>
                  </>
                ) : (
                  <div className="text-2xl font-black text-brand-500 num leading-none whitespace-nowrap">
                    {totalDisplay}
                  </div>
                )}
              </div>
            </div>
          </button>
        );
      })}

      {/* PROMINENT PROMO CODE BUTTON — placed directly beneath the
          plan options so it's the next thing a user sees after
          picking a plan. The CC10 code is shown INLINE as a hint
          because the user can't swipe back to the previous promo
          screen once they are inside the paywall (the state machine
          replaces that screen). Tap opens Apple's native Offer Code
          Redemption Sheet. */}
      {showRedeemCode && (
        <button
          onClick={redeem}
          disabled={busy}
          className="w-full relative overflow-hidden mt-2 bg-white border-2 border-dashed border-brand-400 rounded-2xl p-4 shadow-sm active:scale-[0.98] transition-transform disabled:opacity-60"
          data-testid="native-paywall-redeem-prominent"
        >
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl orange-gradient flex items-center justify-center shadow-sm flex-shrink-0">
              <Ticket className="w-6 h-6 text-white" strokeWidth={2.2} />
            </div>
            <div className="flex-1 text-start min-w-0">
              <div className="text-[10px] font-black text-brand-600 uppercase tracking-widest">
                {en ? "New subscriber · Limited time" : "مشترك جديد · لمدة محدودة"}
              </div>
              <div className="text-base font-black text-neutral-900 leading-tight mt-0.5">
                {en ? "Tap and enter code" : "اضغط واكتب الكود"}
              </div>
            </div>
            <ArrowLeft className="w-4 h-4 text-brand-500 rtl:-scale-x-100 flex-shrink-0" />
          </div>
          {/* The code itself — rendered BIG right inside the card so
              users don't have to go back to the previous screen. */}
          <div className="mt-3 pt-3 border-t border-dashed border-brand-200 flex items-center justify-between gap-3">
            <span className="text-[11px] font-bold text-neutral-500">
              {en ? "Discount code" : "كود الخصم"}
            </span>
            <span
              className="text-xl font-black tracking-[0.3em] text-brand-600 num"
              dir="ltr"
              data-testid="native-paywall-promo-code"
            >
              CC10
            </span>
          </div>
        </button>
      )}

      {error && (
        <div className="text-xs text-red-600 text-center py-2" data-testid="native-paywall-error">
          {error}
        </div>
      )}

      <button
        onClick={buy}
        disabled={busy || !selected}
        className="w-full orange-gradient text-white font-bold py-4 rounded-full shadow-floating active:scale-[0.98] transition-transform text-base flex items-center justify-center gap-2 disabled:opacity-70 mt-4"
        data-testid="native-paywall-buy"
      >
        {busy ? (en ? "Loading..." : "جاري...") : (() => {
          // Server-driven CTA — admins can override via /api/paywall/config.
          // Owner (Feb 2026) pivoted away from the "Start free trial"
          // copy because many Apple regions don't honour the trial on
          // first install. Default is now "Subscribe now" either way.
          const c = pw.cta || {};
          const inner =
            (hasFreeTrial(selected) ? c.text_trial : c.text_no_trial) ||
            c.text_no_trial ||
            (en ? "Subscribe now" : "اشترك الآن");
          return <>{inner} <ArrowLeft className="w-4 h-4 rtl:-scale-x-100" /></>;
        })()}
      </button>

      {selected && hasFreeTrial(selected) && (
        <p className="text-[11px] text-center text-neutral-500 -mt-1">
          {en
            ? <>Then {selected.product?.priceString} — cancel anytime</>
            : <>بعدها {selected.product?.priceString} — يمكنك الإلغاء في أي وقت</>}
        </p>
      )}

      <button
        onClick={restore}
        disabled={busy}
        className="w-full text-center py-2 text-sm text-neutral-500 hover:text-brand-500 disabled:opacity-60 flex items-center justify-center gap-2"
        data-testid="native-paywall-restore"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        {en ? "Restore previous purchase" : "استعادة مشتريات سابقة"}
      </button>

      {/* The small "I have a promo code" link that used to live here
          was moved up — directly under the plan options as a big
          ticket-styled CTA — so new subscribers actually see it. */}

      {pwOpts.show_footer !== false && (
        <div className="text-[10px] text-neutral-400 text-center mt-2 leading-relaxed space-y-1" data-testid="native-paywall-footer">
          <div>
            {pw.footer?.text || (() => {
              const isAndroid = nativePlatform() === "android";
              const store = isAndroid ? (en ? "Google Play" : "Google Play") : "Apple";
              return en
                ? `Payment via ${store} · Subscription renews automatically · Cancel anytime from your ${store} settings`
                : `الدفع عبر ${store} · يتجدد الاشتراك تلقائياً · يمكنك الإلغاء في أي وقت من إعدادات ${store}`;
            })()}
          </div>
          {/* Apple Guideline 3.1.2 — Terms of Service (EULA) and Privacy
              Policy links must be available directly on the paywall,
              not just at signup. Reviewers check for these explicitly.
              We route through `openExternal()` so on native iOS the link
              opens in SFSafariViewController (Apple-approved) instead of
              the app WebView — otherwise reviewers can flag it as an
              in-app external page. */}
          <div className="flex items-center justify-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => openExternal("https://calofay.app/terms")}
              className="underline hover:text-brand-500"
              data-testid="native-paywall-tos-link"
            >
              {en ? "Terms of Service" : "شروط الخدمة"}
            </button>
            <span>·</span>
            <button
              type="button"
              onClick={() => openExternal("https://calofay.app/privacy")}
              className="underline hover:text-brand-500"
              data-testid="native-paywall-privacy-link"
            >
              {en ? "Privacy Policy" : "سياسة الخصوصية"}
            </button>
          </div>
          {/* AI-services disclosure removed per owner request (Feb
              2026). The disclosure still lives on the Privacy Policy
              page (Section 4.6) and that is the surface Apple App
              Review examines; keeping a second copy on the paywall
              was redundant and lengthened the purchase screen. */}
        </div>
      )}

      {/* Promo-code redemption modal — Android + web only. iOS uses
          Apple's native redemption sheet (see `redeem()` above). */}
      {redeemModalOpen && (
        <div
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setRedeemModalOpen(false)}
          data-testid="paywall-redeem-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-neutral-900 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 fade-in duration-200"
            dir={en ? "ltr" : "rtl"}
          >
            <div className="px-6 pt-6 pb-4 text-center">
              <div className="w-14 h-14 rounded-2xl bg-brand-500/10 flex items-center justify-center mx-auto mb-3">
                <Ticket className="w-7 h-7 text-brand-500" />
              </div>
              <div className="text-lg font-black text-neutral-900 dark:text-white mb-1">
                {en ? "Redeem your promo code" : "استخدم رمز الخصم"}
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
                {en
                  ? "Enter your code below. We'll open a secure checkout page — complete payment there and your subscription activates instantly."
                  : "أدخل رمزك بالأسفل. سنفتح لك صفحة دفع آمنة — أكمل عملية الدفع وسيتم تفعيل اشتراكك فوراً."}
              </p>
            </div>
            <div className="px-6 pb-2">
              <input
                type="text"
                value={redeemCodeInput}
                onChange={(e) => setRedeemCodeInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && submitRedeemCode()}
                placeholder={en ? "YOUR CODE" : "الرمز"}
                autoFocus
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                className="w-full bg-neutral-50 dark:bg-neutral-800 border-2 border-neutral-200 dark:border-neutral-700 focus:border-brand-500 rounded-2xl px-5 py-4 text-center text-lg font-black tracking-widest text-neutral-900 dark:text-white focus:outline-none placeholder:text-neutral-300 dark:placeholder:text-neutral-600"
                data-testid="paywall-redeem-input"
              />
            </div>
            <div className="px-6 pt-3 pb-6 flex gap-2">
              <button
                onClick={() => setRedeemModalOpen(false)}
                className="flex-1 py-3 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 font-bold text-sm active:scale-[0.98]"
                data-testid="paywall-redeem-cancel"
              >
                {en ? "Cancel" : "إلغاء"}
              </button>
              <button
                onClick={submitRedeemCode}
                disabled={!redeemCodeInput.trim()}
                className="flex-1 py-3 rounded-full orange-gradient text-white font-black text-sm active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
                data-testid="paywall-redeem-submit"
              >
                {en ? "Continue" : "تابع"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
